import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import type { DashboardWidget, WidgetTemplate } from '@/lib/types/dashboard-widgets'
import { BUILT_IN_WIDGETS } from '@/lib/types/dashboard-widgets'

interface DashboardWidgetContextType {
  widgets: DashboardWidget[]
  addWidget: (widget: Omit<DashboardWidget, 'id' | 'createdAt' | 'updatedAt' | 'order' | 'visible'>) => void
  updateWidget: (id: string, updates: Partial<DashboardWidget>) => void
  removeWidget: (id: string) => void
  reorderWidgets: (widgetIds: string[]) => void
  toggleWidgetVisibility: (id: string) => void
  getWidgetTemplates: () => WidgetTemplate[]
  getWidgetById: (id: string) => DashboardWidget | undefined
}

const DashboardWidgetContext = createContext<DashboardWidgetContextType | undefined>(undefined)

const STORAGE_KEY = 'humblytics_dashboard_widgets'

export function DashboardWidgetProvider({ children, propertyId }: { children: ReactNode; propertyId?: string }) {
  const storageKey = propertyId ? `${STORAGE_KEY}_${propertyId}` : STORAGE_KEY
  const [widgets, setWidgets] = useState<DashboardWidget[]>([])

  // Load widgets from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Convert date strings back to Date objects
        const widgetsWithDates = parsed.map((w: any) => ({
          ...w,
          createdAt: new Date(w.createdAt),
          updatedAt: new Date(w.updatedAt),
        }))
        setWidgets(widgetsWithDates)
      } else {
        // Initialize with default widgets if none exist
        const defaultWidgets: DashboardWidget[] = [
          {
            id: 'default-traffic-chart',
            type: 'chart',
            title: 'Traffic Overview',
            description: 'Pageviews and sessions',
            config: {
              chartType: 'area',
              xAxisKey: 'date',
              dataKeys: ['pageviews', 'sessions'],
              height: 300,
              showLegend: true,
              showGrid: true,
            },
            visible: true,
            order: 0,
            colSpan: 4,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: 'default-key-metrics',
            type: 'metric',
            title: 'Key Metrics',
            config: {
              metrics: [
                { label: 'Pageviews', value: 0 },
                { label: 'Sessions', value: 0 },
              ],
              layout: 'grid',
            },
            visible: true,
            order: 1,
            colSpan: 4,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
        ]
        setWidgets(defaultWidgets)
      }
    } catch (error) {
      console.error('Failed to load widgets from storage:', error)
    }
  }, [storageKey])

  // Save widgets to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(widgets))
    } catch (error) {
      console.error('Failed to save widgets to storage:', error)
    }
  }, [widgets, storageKey])

  const addWidget = useCallback((widget: Omit<DashboardWidget, 'id' | 'createdAt' | 'updatedAt' | 'order' | 'visible'>) => {
    setWidgets((prev) => {
      // Check if widget with same componentType already exists
      const existingWidget = prev.find(
        (w) => w.config?.componentType === widget.config?.componentType && w.type === widget.type
      )
      if (existingWidget) {
        return prev // Don't add duplicate
      }
      
      const newWidget: DashboardWidget = {
        ...widget,
        id: widget.config?.componentType || `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        visible: true,
        order: prev.length,
        createdAt: new Date(),
        updatedAt: new Date(),
        colSpan: widget.colSpan || 2,
        rowSpan: widget.rowSpan || 1,
      }
      return [...prev, newWidget]
    })
  }, [])

  const updateWidget = useCallback((id: string, updates: Partial<DashboardWidget>) => {
    setWidgets((prev) =>
      prev.map((widget) =>
        widget.id === id
          ? { ...widget, ...updates, updatedAt: new Date() }
          : widget
      )
    )
  }, [])

  const removeWidget = useCallback((id: string) => {
    setWidgets((prev) => prev.filter((widget) => widget.id !== id))
  }, [])

  const reorderWidgets = useCallback((widgetIds: string[]) => {
    setWidgets((prev) => {
      const widgetMap = new Map(prev.map((w) => [w.id, w]))
      return widgetIds
        .map((id, index) => {
          const widget = widgetMap.get(id)
          return widget ? { ...widget, order: index, updatedAt: new Date() } : null
        })
        .filter((w): w is DashboardWidget => w !== null)
    })
  }, [])

  const toggleWidgetVisibility = useCallback((id: string) => {
    setWidgets((prev) =>
      prev.map((widget) =>
        widget.id === id
          ? { ...widget, visible: !widget.visible, updatedAt: new Date() }
          : widget
      )
    )
  }, [])

  const getWidgetTemplates = useCallback(() => {
    return BUILT_IN_WIDGETS
  }, [])

  const getWidgetById = useCallback((id: string) => {
    return widgets.find((w) => w.id === id)
  }, [widgets])

  return (
    <DashboardWidgetContext.Provider
      value={{
        widgets,
        addWidget,
        updateWidget,
        removeWidget,
        reorderWidgets,
        toggleWidgetVisibility,
        getWidgetTemplates,
        getWidgetById,
      }}
    >
      {children}
    </DashboardWidgetContext.Provider>
  )
}

function useDashboardWidgets() {
  const context = useContext(DashboardWidgetContext)
  if (context === undefined) {
    throw new Error('useDashboardWidgets must be used within DashboardWidgetProvider')
  }
  return context
}

